import _ from "lodash";
import {
  CollectionContext,
  DataAccessibility,
  DataDeletability,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
  OrganizationVendorSettingsDto,
  ProcessingRegion,
} from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorDto } from "../../common/service/server/dto/VendorDto";
import {
  PrivacyCenterAvailableIntegrationVendorIdsDto,
  PrivacyCenterIntegrationVendorDto,
  PrivacyCenterIntegrationsDto,
} from "../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { ProcessingMethod } from "../../common/service/server/Types";
import {
  VENDOR_CATEGORY,
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER,
  VENDOR_CLASSIFICATION_SLUG_THIRD_PARTY,
  VENDOR_KEY_FB_ADS,
  VENDOR_KEY_GOOGLE_ANALYTICS,
  VENDOR_KEY_GOOGLE_ADS,
  VENDOR_KEY_PAYPAL,
  VENDOR_KEY_SHOPIFY,
  VENDOR_KEY_SHOPIFY_AUDIENCES,
  VENDOR_KEY_TRUEVAULT,
  VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM,
} from "../types/Vendor";
import { DataMapDto } from "../../common/service/server/dto/datamap/DataMapDto";
import { filterDataMapCollection } from "../../common/service/resources/data-map/dataMapTransformers";
import { isUrl } from "./validation";
import {
  RequestHandlingInstructionDto,
  RequestHandlingInstructionEmbeddedDto,
} from "../../common/service/server/dto/RequestHandlingInstructionDto";
import { DataSubjectRequestDetailDto } from "../../common/service/server/dto/DataSubjectRequestDto";
import { RegionGroup } from "../../common/service/server/dto/RegulationRequestTypeDto";
import { Organization } from "../../common/models";

export const DATA_STORAGE_LOCATION_CATEGORY = "Data Storage Location";

export const sortedOrganizationVendors = (vendors: OrganizationDataRecipientDto[]) => {
  if (vendors) {
    const resultVendors: OrganizationDataRecipientDto[] = [];
    const resultVendorIds: string[] = [];
    vendors.forEach((vendor) => {
      if (!resultVendorIds.includes(vendor.vendorId)) {
        resultVendorIds.push(vendor.vendorId);
        resultVendors.push(vendor);
      }
    });
    return resultVendors.sort((a, b) => {
      if (a.status === "In-Progress" && b.status !== "In-Progress") {
        return -1;
      }
      if (b.status === "In-Progress" && a.status !== "In-Progress") {
        return 1;
      }
      if (a.status === b.status) {
        return a.name.localeCompare(b.name);
      }
      return 0;
    });
  }
  return [];
};

interface VendorsByType {
  serviceVendors: OrganizationDataRecipientDto[];
  thirdParty: OrganizationDataRecipientDto[];
  contractors: OrganizationDataRecipientDto[];
  otherVendors: OrganizationDataRecipientDto[];
}

export const vendorsByType = (vendors: OrganizationDataRecipientDto[]): VendorsByType => {
  return vendors.reduce(
    (acc, ov) => {
      if (ov.dataRecipientType === "contractor") {
        acc.contractors.push(ov);
      } else if (
        ov.classification.slug === VENDOR_CLASSIFICATION_SLUG_THIRD_PARTY ||
        ov.serviceProviderRecommendation === "THIRD_PARTY"
      ) {
        acc.thirdParty.push(ov);
      } else if (
        ov.classification.slug === VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER ||
        ov.serviceProviderRecommendation === "SERVICE_PROVIDER"
      ) {
        acc.serviceVendors.push(ov);
      } else {
        acc.otherVendors.push(ov);
      }
      return acc;
    },
    {
      serviceVendors: [] as OrganizationDataRecipientDto[],
      thirdParty: [] as OrganizationDataRecipientDto[],
      contractors: [] as OrganizationDataRecipientDto[],
      otherVendors: [] as OrganizationDataRecipientDto[],
    },
  );
};

type OrgDataRecipientBySellingSharing = {
  selling: OrganizationDataRecipientDto[];
  sharing: OrganizationDataRecipientDto[];
  other: OrganizationDataRecipientDto[];
};

export const vendorsByCcpaSellingOrSharing = (
  dataRecipients: OrganizationDataRecipientDto[],
): OrgDataRecipientBySellingSharing => {
  return _.reduce(
    dataRecipients,
    (result, dr) => {
      if (dr.ccpaIsSelling || dr.isAlwaysDataSellingVendor) {
        result.selling.push(dr);
      } else if (dr.ccpaIsSharing) {
        result.sharing.push(dr);
      } else {
        result.other.push(dr);
      }

      return result;
    },
    {
      selling: [] as OrganizationDataRecipientDto[],
      sharing: [] as OrganizationDataRecipientDto[],
      other: [] as OrganizationDataRecipientDto[],
    },
  );
};

interface RecipientsByGdprProcessor {
  processorRecommendation: OrganizationDataRecipientDto[];
  controllerRecommendation: OrganizationDataRecipientDto[];
  other: OrganizationDataRecipientDto[];
}

export const recipientsByGdprProcessorRec = (
  vendors: OrganizationDataRecipientDto[],
  excludedRecipientCategories?: string[],
): RecipientsByGdprProcessor => {
  return vendors
    .filter((ov) => ov.processingRegions.includes("EEA_UK"))
    .filter(
      (ov) => !excludedRecipientCategories || !excludedRecipientCategories.includes(ov.category),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .reduce(
      (acc, ov) => {
        if (ov.gdprProcessorRecommendation == "Processor") {
          acc.processorRecommendation.push(ov);
        } else if (ov.gdprProcessorRecommendation == "Controller") {
          acc.controllerRecommendation.push(ov);
        } else {
          acc.other.push(ov);
        }
        return acc;
      },
      {
        processorRecommendation: [] as OrganizationDataRecipientDto[],
        controllerRecommendation: [] as OrganizationDataRecipientDto[],
        other: [] as OrganizationDataRecipientDto[],
      },
    );
};

const isGdprProcessorVendor = (vendor: OrganizationDataRecipientDto) => {
  if (vendor.isGdprProcessorSettingLocked) {
    return vendor.gdprProcessorSetting === "Processor";
  }

  if (vendor.gdprProcessorRecommendation === "Processor") {
    return true;
  }

  if (vendor.gdprProcessorSetting !== "Processor") {
    return false;
  }

  return (
    isUrl(vendor.gdprProcessorGuaranteeUrl) || vendor.gdprProcessorGuaranteeFileName?.length > 0
  );
};

const isGdprControllerVendor = (vendor: OrganizationDataRecipientDto) => {
  if (vendor.isGdprProcessorSettingLocked) {
    return vendor.gdprProcessorSetting === "Controller";
  }

  if (vendor.gdprProcessorRecommendation === "Controller") {
    return true;
  }

  if (vendor.gdprProcessorSetting !== "Controller") {
    return false;
  }

  return (
    isUrl(vendor.gdprControllerGuaranteeUrl) || vendor.gdprControllerGuaranteeFileName?.length > 0
  );
};

export const recipientsByGdprProcessorRecOrLockedSetting = (
  vendors: OrganizationDataRecipientDto[],
): RecipientsByGdprProcessor => {
  return vendors
    .filter((ov) => ov.processingRegions.includes("EEA_UK"))
    .reduce(
      (acc, ov) => {
        if (isGdprProcessorVendor(ov)) {
          acc.processorRecommendation.push(ov);
        } else if (isGdprControllerVendor(ov)) {
          acc.controllerRecommendation.push(ov);
        } else {
          acc.other.push(ov);
        }
        return acc;
      },
      {
        processorRecommendation: [] as OrganizationDataRecipientDto[],
        controllerRecommendation: [] as OrganizationDataRecipientDto[],
        other: [] as OrganizationDataRecipientDto[],
      },
    );
};

interface UnknownVendorsByType {
  contractTermsVendors: OrganizationDataRecipientDto[];
  publicTermsVendors: OrganizationDataRecipientDto[];
}

export const unknownVendorsByContract = (
  vendors: OrganizationDataRecipientDto[],
): UnknownVendorsByType => {
  return vendors.reduce(
    (acc, ov) => {
      if (ov.vendorContract?.slug == "found-contract") {
        acc.contractTermsVendors.push(ov);
      } else if (ov.vendorContract?.slug == "use-public") {
        acc.publicTermsVendors.push(ov);
      } else {
        // Error state?
      }
      return acc;
    },
    {
      contractTermsVendors: [] as OrganizationDataRecipientDto[],
      publicTermsVendors: [] as OrganizationDataRecipientDto[],
    },
  );
};

export const organizationVendorIsComplete = (ov) => ov.status === "Completed";

export const organizationVendorIsInProgress = (ov) => ov.status === "In-Progress";

export const getVendorSettingsObject = (
  vendor?: OrganizationDataRecipientDto,
): OrganizationVendorSettingsDto => {
  return {
    contacted: vendor?.contacted || false,
    classificationId: vendor?.classification?.id || "",
    vendorContractId: vendor?.vendorContract?.id || "",
    // @ts-ignore
    vendorContractReviewId: vendor?.vendorContractReviewed?.id || "",
    ccpaIsSelling: vendor?.ccpaIsSelling || false,
    ccpaIsSharing: vendor?.ccpaIsSharing || false,
    automaticallyClassified: vendor?.automaticallyClassified || false,
    isDataStorage: vendor?.isDataStorage || false,
    publicTosUrl: vendor?.publicTosUrl ?? null,

    // file attachment settings
    tosFileKey: vendor?.tosFileKey ?? null,
    tosFileName: vendor?.tosFileName ?? null,

    processingRegions: vendor?.processingRegions,

    gdprProcessorSetting: vendor?.gdprProcessorSetting ?? null,
    gdprProcessorGuaranteeUrl: vendor?.gdprProcessorGuaranteeUrl ?? null,
    gdprProcessorGuaranteeFileKey: vendor?.gdprProcessorGuaranteeFileKey ?? null,
    gdprProcessorGuaranteeFileName: vendor?.gdprProcessorGuaranteeFileName ?? null,
    gdprControllerGuaranteeUrl: vendor?.gdprControllerGuaranteeUrl ?? null,
    gdprControllerGuaranteeFileKey: vendor?.gdprControllerGuaranteeFileKey ?? null,
    gdprControllerGuaranteeFileName: vendor?.gdprControllerGuaranteeFileName ?? null,

    contractStoredInternally: vendor?.contractStoredInternally ?? null,
    gdprContractStoredInternally: vendor?.gdprContractStoredInternally ?? null,

    // For custom vendors
    name: vendor?.name ?? "",
    category: vendor?.category ?? null,
    url: vendor?.url ?? null,
    email: vendor?.email ?? null,

    dataAccessibility: vendor?.dataAccessibility,

    gdprHasSccSetting: vendor?.gdprHasSccSetting ?? null,
    gdprSccUrl: vendor?.gdprSccUrl ?? null,
    gdprSccFileName: vendor?.gdprSccFileName ?? null,
    gdprSccFileKey: vendor?.gdprSccFileKey ?? null,

    removedFromExceptionsToScc: vendor?.removedFromExceptionsToScc ?? false,

    completed: vendor?.completed ?? true,
  };
};

/**
 * Settings that add `collectionContexts` to a vendor's recipient row. The PUT replaces most recipient
 * settings with what it is sent (GDPR, SCC, contract, GPP, classification…), so a vendor that is
 * already a recipient in any context sends its current settings along; a new one needs only the
 * context.
 */
export const addRecipientContextSettings = (
  collectionContexts: CollectionContext[],
  existing?: OrganizationDataRecipientDto | null,
): OrganizationVendorSettingsDto => {
  if (!existing) {
    return { completed: true, collectionContext: collectionContexts };
  }
  // dataAccessibility is the effective value; resending it would pin an inferred answer as the
  // user's own.
  const { dataAccessibility: _dataAccessibility, ...current } = existing;
  return {
    ...current,
    classificationId: existing.classification?.id,
    vendorContractId: existing.vendorContract?.id ?? "",
    vendorContractReviewedId: existing.vendorContractReviewed?.id ?? "",
    completed: true,
    collectionContext: collectionContexts,
  };
};

export const isThirdPartyRecipient = (
  organizationVendor: OrganizationDataRecipientDto | VendorDto,
): boolean => organizationVendor.dataRecipientType == "third_party_recipient";

export const isStorageLocation = (
  organizationVendor: OrganizationDataRecipientDto | VendorDto | null,
): boolean => organizationVendor?.dataRecipientType == "storage_location";

export const isThirdParty = (organizationVendor: OrganizationDataRecipientDto): boolean =>
  ["third-party", "needs-review"].includes(organizationVendor?.classification?.slug);

export const organizationVendorIsSelling = (vendor: OrganizationDataRecipientDto): boolean =>
  (vendor.ccpaIsSelling && vendor.classification?.slug !== "service-provider") ||
  vendor.isAlwaysDataSellingVendor;

export const organizationVendorIsSharing = (vendor: OrganizationDataRecipientDto): boolean =>
  vendor.ccpaIsSharing;

export const isNonSellingSharingThirdParty = (vendor: OrganizationDataRecipientDto): boolean =>
  (isThirdPartyRecipient(vendor) || isThirdParty(vendor)) &&
  !organizationVendorIsSelling(vendor) &&
  !organizationVendorIsSharing(vendor);

export const isAdNetwork = (vendor: OrganizationDataRecipientDto | VendorDto) =>
  vendor.category === VENDOR_CATEGORY_AD_NETWORK;

/**
 * Checks if a vendor is in any of the sharing vendor categories.
 * This matches the backend logic in SellingAndSharingSurveyInterceptor.
 */
export const isSharingVendor = (vendor: OrganizationDataRecipientDto | VendorDto): boolean => {
  const sharingVendorCategories = [
    VENDOR_CATEGORY_AD_NETWORK,
    VENDOR_CATEGORY.SALES_MARKETING_TOOL,
    VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
    VENDOR_CATEGORY.DATA_BROKER,
    "Data Cooperative", // Not in constants but exists in system
  ];

  return sharingVendorCategories.includes(vendor.category);
};

export const isSharingAdNetworkWithAccessibleData = (
  vendor: OrganizationDataRecipientDto,
  instruction: RequestHandlingInstructionDto | RequestHandlingInstructionEmbeddedDto,
): boolean =>
  isAdNetwork(vendor) &&
  organizationVendorIsSharing(vendor) &&
  instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";

export const isBuiltInSourceVendor = (vendor: OrganizationDataSourceDto | VendorDto): boolean =>
  Boolean(vendor.vendorKey?.startsWith("source-") || vendor.vendorKey?.startsWith("truevault-"));

// specific vendor checks

export const isSpecificVendor = (
  vendor: VendorDto | OrganizationDataRecipientDto,
  key: string,
): boolean => vendor.vendorKey == key;
const findSpecificVendor = <T extends VendorDto | OrganizationDataRecipientDto>(
  vendors: T[],
  key: string,
): T | null => vendors.find((v) => isSpecificVendor(v, key)) ?? null;

const createIsSpecificVendor =
  (key: string) => (vendor: VendorDto | OrganizationDataRecipientDto) =>
    isSpecificVendor(vendor, key);

const createFindSpecificVendor =
  (key: string) =>
  <T extends VendorDto | OrganizationDataRecipientDto>(vendors: T[]): T | null =>
    findSpecificVendor(vendors, key);

export const SHOPIFY_APP_VENDOR_ID = "96dd1ca4-cffc-4456-98e3-3b929e0b0860";

export const isShopify = createIsSpecificVendor(VENDOR_KEY_SHOPIFY);
export const findShopify = createFindSpecificVendor(VENDOR_KEY_SHOPIFY);

export const isShopifyAudiences = createIsSpecificVendor(VENDOR_KEY_SHOPIFY_AUDIENCES);
export const findShopifyAudiences = createFindSpecificVendor(VENDOR_KEY_SHOPIFY_AUDIENCES);

export const isGoogleAnalytics = createIsSpecificVendor(VENDOR_KEY_GOOGLE_ANALYTICS);
export const findGoogleAnalytics = createFindSpecificVendor(VENDOR_KEY_GOOGLE_ANALYTICS);

export const isGoogleAds = createIsSpecificVendor(VENDOR_KEY_GOOGLE_ADS);
export const findGoogleAds = createFindSpecificVendor(VENDOR_KEY_GOOGLE_ADS);

export const isFacebookAds = createIsSpecificVendor(VENDOR_KEY_FB_ADS);
export const findFacebookAds = createFindSpecificVendor(VENDOR_KEY_FB_ADS);

export const isPayPal = createIsSpecificVendor(VENDOR_KEY_PAYPAL);
export const findPayPal = createFindSpecificVendor(VENDOR_KEY_PAYPAL);

export const isZendesk = (vendor: VendorDto | OrganizationDataRecipientDto): boolean =>
  vendor?.vendorKey === "zendesk" || vendor?.name === "Zendesk";

export const isTrueVaultConnectedSystem = createIsSpecificVendor(
  VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM,
);

export const RETAIN_BY_DEFAULT_VENDORS = [VENDOR_KEY_TRUEVAULT];

export const defaultProcessingMethod = (
  vendor: OrganizationDataRecipientDto,
): ProcessingMethod | null => {
  if (!vendor) return null;

  if (vendor?.vendorKey && RETAIN_BY_DEFAULT_VENDORS.includes(vendor.vendorKey)) {
    return "RETAIN";
  }

  return null;
};

export const DEFAULT_RETENTION_REASONS_MAP = {
  [VENDOR_KEY_TRUEVAULT]: ["comply-with-a-legal-obligation"],
};

export const defaultVendorRetentionReasons = (vendor: OrganizationDataRecipientDto): string[] => {
  if (!vendor?.vendorKey) return [];

  return _.get(DEFAULT_RETENTION_REASONS_MAP, vendor.vendorKey, []);
};

export const isDataRetentionVendor = (v: OrganizationDataRecipientDto) =>
  v.instructions.some((i) => i.processingMethod === "RETAIN");

export const isCustomAudience = (vendor: OrganizationDataRecipientDto): boolean =>
  vendor.usesCustomAudience === true;

export const isRequestContextApplicable = (
  dataMap: DataMapDto,
  context: CollectionContext,
  v: OrganizationDataRecipientDto,
) => {
  if (dataMap && Array.isArray(dataMap.collection)) {
    const isEmployee = context == "EMPLOYEE";
    const collected = filterDataMapCollection(dataMap, {
      collectionGroupType: isEmployee ? "EMPLOYMENT" : undefined,
      exclude: { collectionGroupType: isEmployee ? undefined : "EMPLOYMENT" },
    });
    const collectionGroupIds = _.uniq(collected.map((c) => c.groupId));
    return dataMap.disclosure
      .filter((d) => collectionGroupIds.includes(d.groupId))
      .map((sv) => sv.vendorId)
      .includes(v.vendorId);
  }
  return false;
};

/**
 * Enhanced vendor filtering function that supports Crunch collection group filtering
 * using the collection groups saved with the data subject request, while maintaining
 * backwards compatibility for all other organizations.
 */
export const isRequestContextApplicableWithCrunchFiltering = (
  dataMap: DataMapDto,
  context: CollectionContext,
  vendor: OrganizationDataRecipientDto,
  request: DataSubjectRequestDetailDto,
  organization: Organization,
) => {
  // Crunch organization public IDs
  const CRUNCH_ORGANIZATION_IDS = ["EER6DTFQX"];
  const isCrunchOrg = CRUNCH_ORGANIZATION_IDS.includes(organization.id);

  // For Crunch orgs with saved collection groups, use union-based filtering
  if (isCrunchOrg && request.consumerGroups && request.consumerGroups.length > 0) {
    if (!dataMap || !Array.isArray(dataMap.collection)) {
      return false;
    }

    // Use union approach: Base consumer/employee group + specific request groups
    const isEmployee = context === "EMPLOYEE";

    // Get BASE tuples of (pic id, collection group id)
    // For now, get all type groups and we'll filter to base programmatically
    const allTypeCollected = filterDataMapCollection(dataMap, {
      collectionGroupType: isEmployee ? "EMPLOYMENT" : "BUSINESS_TO_CONSUMER",
      exclude: { collectionGroupType: isEmployee ? undefined : "EMPLOYMENT" },
    });

    // Filter to only the BASE collection group (e.g., "Consumers" not "Corporate Consumer" or "Franchise Consumer")
    const baseCollected = allTypeCollected.filter((item) => {
      const groupName = dataMap.collectionGroups[item.groupId]?.name || "";
      if (isEmployee) {
        // For employment, look for base employee groups (not specific ones)
        return groupName === "Employees"; // Adjust this logic based on your base employee group name
      } else {
        // For consumer, only include the base "Consumers" group
        return groupName === "Consumers";
      }
    });

    // Get SPECIFIC collection groups from the request
    const consumerGroupIds = request.consumerGroups
      .map((cg) => cg?.id)
      .filter((id): id is string => Boolean(id));

    const specificCollected = filterDataMapCollection(dataMap, {
      collectionGroupType: isEmployee ? "EMPLOYMENT" : undefined,
      exclude: { collectionGroupType: isEmployee ? undefined : "EMPLOYMENT" },
      collectionGroupId: consumerGroupIds,
    });

    // Union both collections (remove duplicates by groupId + picId combination)
    const collected = _.uniqBy(
      [...baseCollected, ...specificCollected],
      (item) => `${item.groupId}-${item.picId}`,
    );

    const collectionGroupIds = _.uniq(collected.map((c) => c.groupId));

    // Get disclosures for these collection groups
    const relevantDisclosures = dataMap.disclosure.filter((d) =>
      collectionGroupIds.includes(d.groupId),
    );

    const disclosedVendorIds = relevantDisclosures.map((sv) => sv.vendorId);
    const isVendorIncluded = disclosedVendorIds.includes(vendor.vendorId);

    return isVendorIncluded;
  }

  // Fall back to original logic for non-Crunch organizations or if no collection groups saved
  return isRequestContextApplicable(dataMap, context, vendor);
};

export const isRequestRegionApplicable = (
  request: DataSubjectRequestDetailDto,
  v: OrganizationDataRecipientDto,
): boolean => {
  const region = request.dataRequestType.regulationLaw.region;
  if (!region) {
    console.error("RegulationLaw missing region data:", request.dataRequestType.regulationLaw);
    return false;
  }

  // Map region group to processing region
  const processingRegion = getProcessingRegionFromRegionGroup(region.regionGroup);
  return v.processingRegions.includes(processingRegion);
};

export const getRequestProcessingRegion = (
  request: DataSubjectRequestDetailDto,
): ProcessingRegion => {
  const region = request.dataRequestType.regulationLaw.region;
  if (!region) {
    const error = new Error(
      `RegulationLaw missing region data: ${JSON.stringify(request.dataRequestType.regulationLaw)}`,
    );
    console.error(error.message);
    throw error;
  }

  return getProcessingRegionFromRegionGroup(region.regionGroup);
};

/**
 * Dynamic helper function to map RegionGroup to ProcessingRegion
 * Replaces hardcoded switch statements with centralized logic
 */
const getProcessingRegionFromRegionGroup = (regionGroup: RegionGroup): ProcessingRegion => {
  switch (regionGroup) {
    case RegionGroup.EEA_UK:
      return "EEA_UK";
    case RegionGroup.CANADA:
      return "CANADA";
    case RegionGroup.UNITED_STATES:
    default:
      return "UNITED_STATES";
  }
};

export const findAlwaysSellingVendors = <T extends VendorDto | OrganizationDataRecipientDto>(
  vendors: T[],
): T[] =>
  vendors.filter((v) => {
    if ("isAlwaysDataSellingVendor" in v) {
      return v.isAlwaysDataSellingVendor;
    } else if ("isAlwaysDataSelling" in v) {
      return v.isAlwaysDataSelling;
    }
    return false;
  });

export const LEGACY_CONTRACTOR_CATEGORY_SENTINEL = "__LEGACY_CONTRACTOR__";
export const isLegacyContractorCategory = (cat?: string) =>
  cat === LEGACY_CONTRACTOR_CATEGORY_SENTINEL;

export type DataAccessibilityIntegrationAvailability =
  | PrivacyCenterIntegrationsDto
  | PrivacyCenterAvailableIntegrationVendorIdsDto;

// Returns the locked data accessibility, or null if the data accessibility is not locked for this vendor
export function isDataAccessibilityLockedForVendor(
  vendor: OrganizationDataRecipientDto,
  apiIntegrations: DataAccessibilityIntegrationAvailability | null | undefined,
): {
  dataAccessibility: DataAccessibility | null;
  dataAccessibilityIsLocked?: boolean;
  dataAccessibilityLockedTooltipMessage?: string;
  dataDeletability?: DataDeletability;
  dataDeletabilityIsLocked?: boolean;
  // dataDeletabilityLockedTooltipMessage?: string;
  deletionProcessingMethod: ProcessingMethod | null;
  deletionProcessingMethodIsLocked?: boolean;
} {
  // The Connected System is TrueVault's handoff into the org's own systems, so its data is always
  // accessible and always deletable (POL-7964). A retained Connected System drops itself -- and
  // every vendor nested under it -- out of the Request to Delete workflow, so the answer is locked
  // here and normalized on the server when the instruction is saved.
  if (isTrueVaultConnectedSystem(vendor)) {
    return {
      dataAccessibility: "ACCESSIBLE",
      dataAccessibilityIsLocked: true,
      dataAccessibilityLockedTooltipMessage:
        "Data is accessible through your Connected System, which processes requests automatically.",
      dataDeletability: "DELETABLE",
      dataDeletabilityIsLocked: true,
      deletionProcessingMethod: "DELETE",
      deletionProcessingMethodIsLocked: true,
    };
  }

  let isAdNetworkVendor = isAdNetwork(vendor);
  let isCustomAudienceVendor = isCustomAudience(vendor);
  let isSellingVendor = vendor.ccpaIsSelling || vendor.isAlwaysDataSellingVendor;
  let isSharingVendor = vendor.ccpaIsSharing;
  let isUploadVendor = vendor.isUploadVendor || vendor.isAlwaysUploadVendor;
  let hasAccessInstructions = vendor.accessInstructions && vendor.accessInstructions.length > 0;
  let hasDeletionInstructions =
    vendor.deletionInstructions && vendor.deletionInstructions.length > 0;

  let vendorApiConnection: PrivacyCenterIntegrationVendorDto | undefined;
  let hasAvailableApiIntegration = false;

  if (apiIntegrations) {
    if ("vendorIds" in apiIntegrations) {
      hasAvailableApiIntegration = apiIntegrations.vendorIds.includes(vendor.vendorId);
    } else {
      vendorApiConnection = (apiIntegrations.vendors || []).find(
        (apiIntegration) => apiIntegration.vendorId == vendor.vendorId,
      );
      hasAvailableApiIntegration = vendorApiConnection != null;
    }
  }

  // If the vendor has an API integration and that integration status is *connected*, then lock both data accessibility and deletability, otherwise if it's *disconnected*, lock only data accessibility and default YES for data deletability
  if (hasAvailableApiIntegration) {
    // Ad networks running custom audiences still have their deletion processing
    // method locked to DELETE even when an API integration exists (POL-7574).
    // Without this, API-connected ad networks like Meta Ads would skip the
    // ad-network locking rules below and leave the delete setting editable.
    // Selling and upload vendors (Rule 2) are locked the same way, which the server
    // also enforces (POL-7588).
    let lockDeletion =
      (isAdNetworkVendor && isCustomAudienceVendor && isSharingVendor) ||
      !!(isSellingVendor && isUploadVendor);

    return {
      dataAccessibility: "ACCESSIBLE",
      dataAccessibilityIsLocked: true,
      dataAccessibilityLockedTooltipMessage: vendorApiConnection?.connected
        ? "Data is accessible through automated connection. To change, disable integration in API settings."
        : "Data is accessible for this vendor. Enable API integration for this vendor to automate data request processing.",
      // dataDeletability: "DELETABLE",
      // dataDeletabilityIsLocked: vendorApiConnection.connected,
      // dataDeletabilityLockedTooltipMessage: vendorApiConnection.connected
      //   ? "Data is accessible through automated connection."
      //   : null,
      dataDeletability: lockDeletion ? "DELETABLE" : undefined,
      dataDeletabilityIsLocked: lockDeletion,
      deletionProcessingMethod: lockDeletion ? "DELETE" : null,
      deletionProcessingMethodIsLocked: lockDeletion,
    };
  }

  // Data Accessibility Rule 2: vendor is selling AND upload. Locking deletability too keeps a stored
  // NOT_DELETABLE from showing "No - unable" over the locked DELETE (POL-7588); same for Rules 5 and 6.
  if (isSellingVendor && isUploadVendor) {
    return {
      dataAccessibility: "ACCESSIBLE",
      dataAccessibilityIsLocked: true,
      dataDeletability: "DELETABLE",
      dataDeletabilityIsLocked: true,
      deletionProcessingMethod: "DELETE",
      deletionProcessingMethodIsLocked: true,
    };
  }

  // Data Accessibility Rules 5 and 6: ad network => data accessibility determined by sharing and custom audience
  if (isAdNetworkVendor) {
    if (isCustomAudienceVendor && isSharingVendor) {
      return {
        dataAccessibility: "ACCESSIBLE",
        dataAccessibilityIsLocked: true,
        dataDeletability: "DELETABLE",
        dataDeletabilityIsLocked: true,
        deletionProcessingMethod: "DELETE",
        deletionProcessingMethodIsLocked: true,
        // deletionProcessingMethodLocked: null,
      };
    } else if (!isCustomAudienceVendor) {
      return {
        dataAccessibility: "NOT_ACCESSIBLE",
        dataAccessibilityIsLocked: true,
        dataAccessibilityLockedTooltipMessage:
          "Data is not accessible for ad networks that do not receive an uploaded list of customers or contacts.",
        // dataDeletability: null,
        // dataDeletabilityIsLocked: true,
        deletionProcessingMethod: "DELETE",
        deletionProcessingMethodIsLocked: true,
      };
    }
  }

  // For remaining vendors, use presence of access instructions and/or deletion instructions to determine if data accessibility is locked
  return {
    dataAccessibility: hasAccessInstructions || hasDeletionInstructions ? "ACCESSIBLE" : null,
    dataAccessibilityIsLocked: hasAccessInstructions || hasDeletionInstructions,
    dataAccessibilityLockedTooltipMessage:
      hasAccessInstructions || hasDeletionInstructions
        ? "Our research shows that this vendor has publicly available instructions on accessing stored data."
        : null,
    // dataDeletability: null,
    deletionProcessingMethod: null,
    deletionProcessingMethodIsLocked: false,
    // dataDeletabilityLockedTooltipMessage: null,
    // deletionProcessingMethodLocked: null,
  };
}

/**
 * Resolves the accessibility value shown in Data Storage and used by request workflows.
 * Locked values take precedence over the organization's persisted value.
 */
export function resolveDataAccessibilityForVendor(
  vendor: OrganizationDataRecipientDto,
  apiIntegrations: DataAccessibilityIntegrationAvailability | null | undefined,
) {
  const lockedValues = isDataAccessibilityLockedForVendor(vendor, apiIntegrations);

  return {
    ...lockedValues,
    effectiveDataAccessibility: lockedValues.dataAccessibility ?? vendor.dataAccessibility,
  };
}

/**
 * Null accessibility remains implicitly accessible for workflows. Only a resolved explicit
 * NOT_ACCESSIBLE value excludes the vendor.
 */
export function isVendorDataAccessibleForWorkflow(
  vendor: OrganizationDataRecipientDto,
  apiIntegrations: DataAccessibilityIntegrationAvailability | null | undefined,
): boolean {
  return (
    resolveDataAccessibilityForVendor(vendor, apiIntegrations).effectiveDataAccessibility !==
    "NOT_ACCESSIBLE"
  );
}

/**
 * Whether a vendor can stand in for others as a "Data retrieved via" source (DES-43): its own Access data has to be
 * reachable and not marked "not stored", the rule that gives it a row on Access requests (RetrieveInfo's
 * belongsInRetrieveInfoStep, minus the request-specific checks). Pages that edit the Access instruction locally pass
 * their current `knowInstruction`; otherwise the vendor's saved one is used.
 */
export function canBeAccessDataSource(
  vendor: OrganizationDataRecipientDto,
  integrationAvailability: DataAccessibilityIntegrationAvailability | null | undefined,
  knowInstruction: { processingMethod?: string | null } | undefined = vendor.instructions?.find(
    (instruction) => instruction.requestType === "KNOW",
  ),
): boolean {
  return (
    isVendorDataAccessibleForWorkflow(vendor, integrationAvailability) &&
    knowInstruction?.processingMethod !== "INACCESSIBLE_OR_NOT_STORED"
  );
}

// Returns true iff the given combination of vendor settings is complete
export function isDataAccessibilityAndDeletionSettingsCompletedForVendor({
  dataAccessibility,
  dataDeletability,
  deletionProcessingMethod,
  retentionReasons,
  hasRetentionExceptions,
  deletedPIC,
}: {
  dataAccessibility: DataAccessibility;
  dataDeletability: DataDeletability;
  deletionProcessingMethod: ProcessingMethod;
  retentionReasons: string[];
  hasRetentionExceptions: boolean;
  deletedPIC: string[];
}): boolean {
  if (dataAccessibility == null) {
    return false;
  }

  // Easy case: if data accessibility is NOT_ACCESSIBLE, or if the vendor's data cannot technically be deleted, then nothing else is required
  if (dataAccessibility == "NOT_ACCESSIBLE" || dataDeletability == "NOT_DELETABLE") {
    return true;
  }

  // Always require a deletion processing method
  if (deletionProcessingMethod == null) {
    return false;
  }

  // If the vendor's data will be deleted or inaccessible, then nothing else is required
  if (
    deletionProcessingMethod == "DELETE" ||
    deletionProcessingMethod == "INACCESSIBLE_OR_NOT_STORED"
  ) {
    return true;
  }

  // From here, we know that (1) vendor's data is accessible and (2) vendor's data will be retained

  // Since vendor data will be retained, need at least one retention reason
  if ((retentionReasons || []).length == 0) {
    return false;
  }

  // If vendor has retention exceptions (i.e. partial deletion), then need at least one deleted PIC
  if (hasRetentionExceptions && (deletedPIC || []).length == 0) {
    return false;
  }

  return true;
}

// Returns true iff the vendor's data storage settings maps to "Partial - some data deleted"
export function isVendorDeletabilityConfiguredForPartialDeletion(
  vendor: OrganizationDataRecipientDto,
): boolean {
  let deleteInstruction = vendor.instructions?.find((i) => i.requestType == "DELETE");
  // If no delete instruction, then we can't make a determination either way, so throw an error
  if (!deleteInstruction) {
    throw new Error(`No delete instruction found for vendor ${vendor.name} (${vendor.vendorId})`);
  }

  // Note that in theory we could only check for `hasRetentionExceptions` being true, but let's be extra strict and check that all the other settings are also as expected
  return (
    deleteInstruction.processingMethod === "RETAIN" &&
    deleteInstruction.hasRetentionExceptions === true &&
    vendor.dataDeletability === "DELETABLE"
  );
}
